import React, { useState, useEffect } from 'react';
import DialogCard from './DialogCard';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import apiClient from '../api/client';
import { copyText } from '../utils/clipboard';

// Focused "connect your calendar" screen. Reached from:
//  • the landing Apple/calendar CTA → /?connect=calendar → (after login) here
//  • a prominent "Collega calendari" button in the app (Calendari page)
// Shows the personal subscribe link with one-tap Subscribe (webcal) + Copy.

// Regenerating invalidates the OLD link: calendars already subscribed with it
// silently stop updating. Always confirm first (an accidental tap here is very
// hard for a non-technical user to diagnose).
export const REGEN_CONFIRM = {
  it: 'Rigenerare il link?\n\nI calendari già collegati (Apple, Google, Outlook) smetteranno di aggiornarsi e andranno ricollegati con il nuovo link.',
  en: 'Regenerate the link?\n\nCalendars already connected (Apple, Google, Outlook) will stop updating and must be reconnected with the new link.',
  es: '¿Regenerar el enlace?\n\nLos calendarios ya conectados (Apple, Google, Outlook) dejarán de actualizarse y habrá que reconectarlos con el nuevo enlace.',
  de: 'Link neu erstellen?\n\nBereits verbundene Kalender (Apple, Google, Outlook) werden nicht mehr aktualisiert und müssen mit dem neuen Link neu verbunden werden.',
  fr: 'Régénérer le lien ?\n\nLes calendriers déjà connectés (Apple, Google, Outlook) cesseront de se mettre à jour et devront être reconnectés avec le nouveau lien.',
  nl: 'Link opnieuw genereren?\n\nReeds gekoppelde agenda’s (Apple, Google, Outlook) worden niet meer bijgewerkt en moeten met de nieuwe link opnieuw worden gekoppeld.',
  pt: 'Regenerar o link?\n\nOs calendários já ligados (Apple, Google, Outlook) deixarão de se atualizar e terão de ser ligados de novo com o novo link.',
  ru: 'Создать новую ссылку?\n\nУже подключённые календари (Apple, Google, Outlook) перестанут обновляться — их нужно будет подключить заново по новой ссылке.',
  pl: 'Wygenerować link ponownie?\n\nJuż połączone kalendarze (Apple, Google, Outlook) przestaną się aktualizować i trzeba będzie je połączyć nowym linkiem.',
  ro: 'Regenerezi linkul?\n\nCalendarele deja conectate (Apple, Google, Outlook) nu se vor mai actualiza și vor trebui reconectate cu noul link.',
  zh: '重新生成链接？\n\n已连接的日历（Apple、Google、Outlook）将停止更新，需要用新链接重新连接。',
  ja: 'リンクを再生成しますか？\n\n接続済みのカレンダー（Apple、Google、Outlook）は更新されなくなり、新しいリンクで再接続が必要になります。',
  hi: 'लिंक फिर से बनाएँ?\n\nपहले से जुड़े कैलेंडर (Apple, Google, Outlook) अपडेट होना बंद हो जाएँगे और नए लिंक से दोबारा जोड़ने होंगे।',
  ko: '링크를 다시 생성할까요?\n\n이미 연결된 캘린더(Apple, Google, Outlook)는 업데이트가 중단되며 새 링크로 다시 연결해야 합니다.',
  ar: 'إعادة إنشاء الرابط؟\n\nستتوقف التقويمات المتصلة (Apple وGoogle وOutlook) عن التحديث ويجب إعادة ربطها بالرابط الجديد.',
};

// Words for the things this screen could not previously admit had happened.
const LOCAL = {
  it: { loadFailed: 'Non sono riuscita a preparare il link.', retry: 'Riprova', copyFailed: 'Non sono riuscita a copiarlo: tocca il link qui sopra e copialo a mano.', regenFailed: 'Non ha funzionato: il link è ancora quello di prima.', keep: 'Lascia stare' },
  en: { loadFailed: "Couldn't prepare the link.", retry: 'Retry', copyFailed: "Couldn't copy it: tap the link above and copy it by hand.", regenFailed: "That didn't work: the link is still the old one.", keep: 'Leave it' },
  es: { loadFailed: 'No he podido preparar el enlace.', retry: 'Reintentar', copyFailed: 'No he podido copiarlo: toca el enlace de arriba y cópialo a mano.', regenFailed: 'No ha funcionado: el enlace sigue siendo el anterior.', keep: 'Déjalo' },
  de: { loadFailed: 'Der Link konnte nicht erstellt werden.', retry: 'Erneut versuchen', copyFailed: 'Kopieren hat nicht geklappt: tippe oben auf den Link und kopiere ihn von Hand.', regenFailed: 'Hat nicht geklappt: der Link ist noch der alte.', keep: 'Lassen' },
  fr: { loadFailed: "Impossible de préparer le lien.", retry: 'Réessayer', copyFailed: "La copie n'a pas fonctionné : touchez le lien ci-dessus et copiez-le à la main.", regenFailed: "Ça n'a pas marché : le lien est toujours l'ancien.", keep: 'Laisser' },
  nl: { loadFailed: 'Kon de link niet klaarmaken.', retry: 'Opnieuw', copyFailed: 'Kopiëren lukte niet: tik op de link hierboven en kopieer hem zelf.', regenFailed: 'Dat lukte niet: de link is nog de oude.', keep: 'Laat maar' },
  pt: { loadFailed: 'Não consegui preparar o link.', retry: 'Tentar de novo', copyFailed: 'Não consegui copiar: toca no link acima e copia-o à mão.', regenFailed: 'Não funcionou: o link continua a ser o antigo.', keep: 'Deixa estar' },
  ru: { loadFailed: 'Не удалось подготовить ссылку.', retry: 'Повторить', copyFailed: 'Скопировать не удалось: нажмите на ссылку выше и скопируйте вручную.', regenFailed: 'Не получилось: ссылка осталась прежней.', keep: 'Оставить' },
  pl: { loadFailed: 'Nie udało się przygotować linku.', retry: 'Ponów', copyFailed: 'Nie udało się skopiować: dotknij linku powyżej i skopiuj ręcznie.', regenFailed: 'Nie udało się: link jest wciąż ten sam.', keep: 'Zostaw' },
  ro: { loadFailed: 'Nu am putut pregăti linkul.', retry: 'Reîncearcă', copyFailed: 'Nu am putut copia: atinge linkul de mai sus și copiază-l manual.', regenFailed: 'Nu a mers: linkul este tot cel vechi.', keep: 'Lasă' },
  zh: { loadFailed: '无法生成链接。', retry: '重试', copyFailed: '复制失败：请点击上方链接手动复制。', regenFailed: '没有成功：链接仍是原来的。', keep: '算了' },
  ja: { loadFailed: 'リンクを用意できませんでした。', retry: '再試行', copyFailed: 'コピーできませんでした。上のリンクをタップして手動でコピーしてください。', regenFailed: 'うまくいきませんでした。リンクは以前のままです。', keep: 'やめる' },
  hi: { loadFailed: 'लिंक तैयार नहीं हो सका।', retry: 'फिर कोशिश', copyFailed: 'कॉपी नहीं हो सका: ऊपर लिंक को छूकर हाथ से कॉपी करें।', regenFailed: 'काम नहीं हुआ: लिंक अब भी पुराना है।', keep: 'रहने दें' },
  ko: { loadFailed: '링크를 준비하지 못했습니다.', retry: '다시 시도', copyFailed: '복사하지 못했습니다. 위 링크를 눌러 직접 복사하세요.', regenFailed: '실패했습니다. 링크는 이전 그대로입니다.', keep: '그대로 두기' },
  ar: { loadFailed: 'تعذّر تجهيز الرابط.', retry: 'إعادة المحاولة', copyFailed: 'تعذّر النسخ: المس الرابط بالأعلى وانسخه يدويًا.', regenFailed: 'لم تنجح العملية: الرابط لا يزال القديم.', keep: 'اتركه' },
};

export default function ConnectCalendar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const lang = user?.language || 'it';
  const T = useT(lang);
  const s = T.settings || {};
  const [feed, setFeed] = useState(null);
  const [copyStatus, setCopyStatus] = useState('idle'); // idle | ok | fail
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [askRegen, setAskRegen] = useState(false);
  const [regenFailed, setRegenFailed] = useState(false);
  const L = LOCAL[(lang || 'it').split('-')[0]] || LOCAL.en;

  useEffect(() => {
    let alive = true;
    setLoadFailed(false);
    apiClient.get('/events/ics-feed')
      .then(r => { if (alive) setFeed(r.data); })
      // Without this the box showed "…" for ever and both buttons stayed inert:
      // a screen that never finishes, with nothing to say why or what to do.
      .catch(() => { if (alive) setLoadFailed(true); })
    return () => { alive = false; };
  }, [attempt]);

  const [regenerating, setRegenerating] = useState(false);

  async function copyFeed() {
    if (!feed?.https) return;
    // It used to announce "Copied ✓" the instant it was tapped: writeText
    // returns a promise, so the try/catch around it caught nothing and a
    // failed copy was reported as a success.
    const ok = await copyText(feed.https);
    setCopyStatus(ok ? 'ok' : 'fail');
    setTimeout(() => setCopyStatus('idle'), 2500);
  }

  function regenerate() {
    if (regenerating) return;
    setRegenFailed(false);
    setAskRegen(true);   // asked in the app's own voice, see the modal below
  }

  function doRegenerate() {
    setAskRegen(false);
    setRegenerating(true);
    apiClient.post('/events/ics-feed/regenerate')
      .then(r => setFeed(r.data))
      // A silent failure here left the OLD link on screen, so you would think
      // the old one had been retired when it had not.
      .catch(() => setRegenFailed(true))
      .finally(() => setRegenerating(false));
  }

  return (
    <div className="login-page" style={{ justifyContent: 'center', padding: '24px 18px', minHeight: '100dvh', gap: 6 }}>
      {/* Regenerating quietly breaks every calendar already subscribed with the
          old link. Worth asking properly — and worth asking in the app's own
          voice rather than a system box, which is easy to dismiss by reflex. */}
      {askRegen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}
          onClick={() => setAskRegen(false)}
        >
          <DialogCard onClick={e => e.stopPropagation()} style={{
            background: 'var(--color-card)', borderRadius: 'var(--radius)',
            padding: '22px 20px', width: '100%', maxWidth: 360,
            boxShadow: '0 8px 32px rgba(0,0,0,0.22)', textAlign: 'start',
          }}>
            <p style={{ margin: '0 0 18px', fontSize: '0.9rem', color: 'var(--color-text)', lineHeight: 1.5, whiteSpace: 'pre-line' }}>
              {REGEN_CONFIRM[(lang || 'it').split('-')[0]] || REGEN_CONFIRM.en}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button type="button" className="btn btn-outline" onClick={() => setAskRegen(false)}>
                {L.keep}
              </button>
              <button type="button" className="btn btn-outline"
                style={{ borderColor: 'var(--color-red)', color: 'var(--color-red)' }}
                onClick={doRegenerate}>
                ↻ {s.feedRegenerate}
              </button>
            </div>
          </DialogCard>
        </div>
      )}

      <div style={{ fontSize: '3rem', lineHeight: 1 }} aria-hidden="true">📆</div>
      <h1 className="login-title" style={{ textAlign: 'center', margin: '6px 0 2px' }}>{s.feedTitle}</h1>
      <p className="login-subtitle" style={{ textAlign: 'center', maxWidth: 380, margin: '0 0 18px' }}>{s.feedDesc}</p>

      <div style={{ width: '100%', maxWidth: 420 }}>
        <input readOnly value={feed?.https || '…'} onFocus={(e) => e.target.select()}
          style={{ width: '100%', boxSizing: 'border-box', border: '2px solid var(--color-primary)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', fontSize: '0.78rem', color: 'var(--color-text)', background: 'var(--color-card)', marginBottom: 10 }} />
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" onClick={copyFeed} disabled={!feed}
            style={{ flex: 1, minWidth: 130, padding: '13px 12px', borderRadius: 'var(--radius-sm)', border: '2px solid var(--color-primary)', background: 'var(--color-card)', color: 'var(--color-primary)', fontWeight: 700, fontSize: '0.9rem', cursor: feed ? 'pointer' : 'default' }}>
            {copyStatus === 'ok' ? s.feedCopied : s.feedCopy}
          </button>
          <a href={feed?.webcal || '#'} aria-disabled={!feed}
            style={{ flex: 1, minWidth: 130, textAlign: 'center', padding: '13px 12px', borderRadius: 'var(--radius-sm)', border: '2px solid var(--color-primary)', background: 'var(--color-primary)', color: '#fff', fontWeight: 700, fontSize: '0.9rem', textDecoration: 'none', opacity: feed ? 1 : 0.6 }}>
            {s.feedSubscribe}
          </a>
        </div>
        {copyStatus === 'fail' && (
          <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: 10, lineHeight: 1.45 }}>{L.copyFailed}</div>
        )}
        {loadFailed && (
          <div style={{ marginTop: 12, fontSize: '0.85rem', color: 'var(--color-red)', lineHeight: 1.45 }}>
            {L.loadFailed}{' '}
            <button type="button" onClick={() => setAttempt(a => a + 1)}
              style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--color-primary-dark)', textDecoration: 'underline', cursor: 'pointer' }}>
              {L.retry}
            </button>
          </div>
        )}
        {regenFailed && (
          <div style={{ marginTop: 12, fontSize: '0.85rem', color: 'var(--color-red)', lineHeight: 1.45 }}>{L.regenFailed}</div>
        )}
        <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', lineHeight: 1.45, marginTop: 14 }}>{s.feedHint}</div>
        {feed && s.feedRegenerate && (
          <button type="button" onClick={regenerate} disabled={regenerating}
            style={{ marginTop: 10, background: 'none', border: 'none', color: 'var(--color-text-muted)', fontSize: '0.8rem', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', padding: 0, opacity: regenerating ? 0.5 : 1 }}>
            ↻ {s.feedRegenerate}
          </button>
        )}
      </div>

      <button type="button" onClick={() => navigate('/')}
        style={{ marginTop: 26, background: 'none', border: 'none', color: 'var(--color-text-muted)', fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'inherit' }}>
        {T.form?.cancelButton || T.welcome?.continue || 'OK'}
      </button>
    </div>
  );
}
